/**
 * Simulation entry point. step() runs all systems in fixed order, then flushes
 * dead entities and advances the tick. Deterministic: same inputs -> same state.
 */
import type { World } from "./world";
import type { Registry } from "../data/registry";
import { constructionSystem } from "./systems/construction";
import { gatheringSystem } from "./systems/gathering";
import { farmSystem } from "./systems/farm";
import { productionSystem } from "./systems/production";
import { transportSystem } from "./systems/transport";
import { combatSystem } from "./systems/combat";
import { towerSystem } from "./systems/tower";
import { faunaSystem } from "./systems/fauna";
import { healingSystem } from "./systems/healing";
import { movementSystem } from "./systems/movement";

export { World } from "./world";
export { RNG } from "./rng";
export { createMatch, type PlayerConfig, type MatchOptions } from "./match";
export { executeCommand } from "./commands";
export { spawnUnit, spawnBuilding, spawnResourceNode, spawnAnimal } from "./spawn";
export { findPath } from "./pathfinding";
export { snapshot, snapshotJSON, restore, type WorldSnapshot } from "./serialize";

export function step(world: World, reg: Registry): void {
  constructionSystem(world, reg);
  gatheringSystem(world, reg);
  farmSystem(world, reg);
  productionSystem(world, reg);
  transportSystem(world, reg);
  combatSystem(world, reg);
  towerSystem(world, reg); // garrisoned towers fire (reuses combat's fresh spatial index)
  faunaSystem(world, reg);
  healingSystem(world, reg);
  movementSystem(world, reg);

  resolveDefeats(world);

  world.flushDead();
  world.tick++;
}

/**
 * Mark players who have lost the game. Defeat = no completed town center AND no
 * units (a player with neither can never recover).
 *
 * Evaluated over ALL registered players, NOT just those that still own entities:
 * a player wiped to ZERO entities has no entry in the entity-derived counts, and
 * that "lost everything" case is precisely when defeat must fire. Iterating the
 * counts alone (the old bug) silently skipped fully-wiped players, so no
 * DEFEAT/VICTORY screen ever showed — and it stayed wrong across a save/reload.
 */
export function resolveDefeats(world: World): void {
  const counts = new Map<number, { units: number; core: number }>();
  for (const [eid, ident] of world.ident) {
    const o = world.owner.get(eid)?.player;
    if (o === undefined) continue;
    const c = counts.get(o) ?? { units: 0, core: 0 };
    if (ident.kind === "unit") c.units++;
    else if (ident.kind === "building") {
      // "core" is data-driven (BuildingDef.isCore → Building.isCore), so a modded
      // civ whose core isn't a town_center still has a real loss condition.
      const b = world.building.get(eid);
      if (b && b.isCore && !b.isFoundation) c.core++;
    }
    counts.set(o, c);
  }
  // Iterate ALL registered players, not the entity-derived counts: a fully-wiped
  // player has NO counts entry, and that is exactly when defeat must fire.
  for (const [, ps] of world.players) {
    if (ps.defeated) continue;
    const c = counts.get(ps.id) ?? { units: 0, core: 0 };
    if (c.core === 0 && c.units === 0) {
      ps.defeated = true;
      ps.alive = false;
    }
  }
}
